// Per-button "On click" picker. Embedded inside the existing ButtonSpecEditor
// in blockEditors.tsx. Lets the author choose between five intents:
//   - none
//   - external_url (also covers the legacy `url` field)
//   - navigate (to another navigation entry)
//   - set_inputs (literal name/value pairs)
//   - run_action (named PageAction)

import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { FIELD_LABEL, HINT, INPUT, OptionalNumber, SUBSECTION, TWO_COL } from '../blockEditors'
import type { ArgMapping, ButtonClick, ButtonSpec, EmbedMode, Section } from '../types'
import { walkBlocks } from '../pageOps'
import { usePageRuntime } from './runtime'
import {
  loadDashboardObject,
  loadPageObject,
  loadTransactionObject,
  loadVisualObject,
  listProjectDashboards,
  listProjectPages,
  listProjectVisuals,
} from '@/api/designers'
import { getJsonRaw } from '@/api/generateapp'
import ArgMappingEditor, { type ArgTargetOption } from '../ArgMappingEditor'
import ObjectPickerField from '../ObjectPickerField'
import { extractDashboardTargets, extractPageTargets, extractVisualTargets } from '../PropertyPanel'

interface Props {
  spec: ButtonSpec
  // Optional set of available navigation targets (when known — e.g. from
  // application.json). Keep it loose; the editor falls back to free-text input
  // when the list is empty.
  navTargets?: { obj_type: string; obj_name: string; title?: string }[]
  onChange: (next: ButtonSpec) => void
}

type Kind = ButtonClick['kind']

const KIND_LABEL: Record<Kind, string> = {
  none:         'None (decorative)',
  external_url: 'Open URL',
  scroll_to:    'Scroll to section',
  navigate:     'Navigate to object',
  open_popup:   'Open pop-up',
  set_inputs:   'Set page input(s)',
  toggle_var:   'Toggle a variable',
  copy:         'Copy text',
  play_media:   'Play / pause a video',
  run_action:   'Run page action',
  add_to_cart:  'Add to the cart',
  sign_out:     'Sign out',
}

function defaultsFor(kind: Kind, prev?: ButtonClick, btn?: ButtonSpec): ButtonClick {
  switch (kind) {
    case 'none': return { kind: 'none' }
    case 'external_url':
      return {
        kind: 'external_url',
        url: (prev && prev.kind === 'external_url' ? prev.url : undefined) || btn?.url || '#',
        newTab: btn?.newTab,
      }
    case 'navigate':
      return prev?.kind === 'navigate'
        ? prev
        : { kind: 'navigate', obj_type: 'C', obj_name: '' }
    case 'set_inputs':
      return prev?.kind === 'set_inputs' ? prev : { kind: 'set_inputs', values: {} }
    case 'run_action':
      return prev?.kind === 'run_action' ? prev : { kind: 'run_action', actionName: '' }
    case 'scroll_to':
      return prev?.kind === 'scroll_to' ? prev : { kind: 'scroll_to', target: '', smooth: true }
    case 'open_popup':
      return prev?.kind === 'open_popup' ? prev : { kind: 'open_popup', sectionId: '' }
    case 'toggle_var':
      return prev?.kind === 'toggle_var' ? prev : { kind: 'toggle_var', name: '' }
    case 'copy':
      return prev?.kind === 'copy' ? prev : { kind: 'copy', text: '', confirm: 'Copied' }
    case 'play_media':
      return prev?.kind === 'play_media' ? prev : { kind: 'play_media', blockId: '', action: 'toggle' }
    case 'sign_out':
      return prev?.kind === 'sign_out' ? prev : { kind: 'sign_out' }
    case 'add_to_cart':
      return prev?.kind === 'add_to_cart' ? prev : {
        kind: 'add_to_cart', sku: '', name: '', price: '', qty: 1,
        addedLabel: 'Added to cart',
      }
    default:
      // Exhaustive today; the fallback keeps an unknown persisted kind from
      // crashing the editor if the schema grows ahead of this file.
      return { kind: 'none' }
  }
}

// Resolve current intent — fall back to the legacy `url` field when on_click
// is absent so existing buttons keep working without a forced migration.
function currentClick(btn: ButtonSpec): ButtonClick {
  if (btn.on_click) return btn.on_click
  if (btn.url) return { kind: 'external_url', url: btn.url, newTab: btn.newTab }
  return { kind: 'none' }
}

/**
 * Every anchor target on the current page, so `scroll_to` offers a dropdown
 * of what actually exists instead of asking the author to type an id and
 * hope. Walks sections AND their block trees, since either can carry one.
 */
function collectAnchors(sections: Section[]): { id: string; label: string }[] {
  const out: { id: string; label: string }[] = []
  for (const sec of sections || []) {
    const name = sec.name || sec.tag || sec.id
    if (sec.anchorId) out.push({ id: sec.anchorId, label: `${name} (#${sec.anchorId})` })
    // `walkBlocks` recurses into a block's child slots by TYPE (tab items,
    // accordion items, column blocks, `children`). Do NOT hand-roll this:
    // `columns` is an array on a `columns` block but a COUNT on card_grid /
    // feature_grid / stats / pricing / gallery, so a generic walk crashes.
    walkBlocks(sec.blocks ?? [], (b) => {
      if (b.anchorId) out.push({ id: b.anchorId, label: `${name} › ${b.type} (#${b.anchorId})` })
    })
  }
  return out
}

export default function OnClickEditor({ spec, navTargets, onChange }: Props) {
  const runtime = usePageRuntime()
  const actions = runtime.getActions()
  const sections = useMemo(() => runtime.getSections?.() || [], [runtime])
  const anchors = useMemo(() => collectAnchors(sections), [sections])
  const popupSections = useMemo(
    () => sections
      .filter((s) => s.tag === 'popup')
      .map((s) => ({ id: s.id, label: s.name || s.popup?.modalTitle || s.id })),
    [sections],
  )
  const click = currentClick(spec)
  const setClick = (next: ButtonClick) => {
    // When the user picks anything other than external_url, keep the legacy
    // `url` field as-is (don't lose it on disk) but stop reading from it.
    onChange({ ...spec, on_click: next })
  }
  const setKind = (kind: Kind) => setClick(defaultsFor(kind, click, spec))
  // A saved kind this editor does not know (agent drift, or a schema ahead of
  // this file) is listed as itself rather than the select silently showing
  // the first option over it; its settings stay on disk until another kind
  // is picked.
  const kindKnown = Object.prototype.hasOwnProperty.call(KIND_LABEL, click.kind)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div>
        <label style={FIELD_LABEL}>On click</label>
        <select value={click.kind} onChange={(e) => setKind(e.target.value as Kind)} style={INPUT}>
          {!kindKnown && <option value={click.kind}>{String(click.kind)} (unsupported)</option>}
          {(Object.keys(KIND_LABEL) as Kind[]).map((k) => (
            <option key={k} value={k}>{KIND_LABEL[k]}</option>
          ))}
        </select>
        {!kindKnown && (
          <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-error, #cf222e)' }}>
            "{String(click.kind)}" is not a click kind the runtime supports, so this button does nothing.
            Pick one above to replace it.
          </div>
        )}
      </div>

      {click.kind === 'external_url' && (
        <>
          <div>
            <label style={FIELD_LABEL}>URL</label>
            <input
              value={click.url || ''}
              onChange={(e) => setClick({ ...click, url: e.target.value })}
              placeholder="https://… or /api/…"
              style={INPUT}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!click.newTab}
              onChange={(e) => setClick({ ...click, newTab: e.target.checked })}
            />
            Open in new tab
          </label>
        </>
      )}

      {click.kind === 'scroll_to' && (
        <>
          <div>
            <label style={FIELD_LABEL}>Target anchor</label>
            <select
              value={click.target || ''}
              onChange={(e) => setClick({ ...click, target: e.target.value })}
              style={INPUT}
            >
              <option value="">— Select a section —</option>
              {anchors.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
            <input
              value={click.target || ''}
              onChange={(e) => setClick({ ...click, target: e.target.value })}
              placeholder="or type an anchor id, e.g. pricing"
              style={{ ...INPUT, marginTop: 6, fontFamily: 'monospace' }}
            />
            {anchors.length === 0 && (
              <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                No anchors yet — set an "Anchor id" on a section or block to make it a target.
              </div>
            )}
          </div>
          <div>
            <label style={FIELD_LABEL}>Offset (px)</label>
            <input
              type="number"
              value={click.offset ?? ''}
              onChange={(e) => setClick({ ...click, offset: e.target.value === '' ? undefined : Number(e.target.value) })}
              placeholder="auto (clears a sticky header)"
              style={INPUT}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={click.smooth !== false}
              onChange={(e) => setClick({ ...click, smooth: e.target.checked })}
            />
            Smooth scroll
          </label>
        </>
      )}

      {click.kind === 'open_popup' && (
        <div>
          <label style={FIELD_LABEL}>Pop-up section</label>
          <select
            value={click.sectionId || ''}
            onChange={(e) => setClick({ ...click, sectionId: e.target.value })}
            style={INPUT}
          >
            <option value="">— Select —</option>
            {click.sectionId && !popupSections.some((p) => p.id === click.sectionId) && (
              <option value={click.sectionId}>{click.sectionId} (not found)</option>
            )}
            {popupSections.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
          {popupSections.length === 0 && (
            <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              No pop-up sections on this page yet.
            </div>
          )}
        </div>
      )}

      {click.kind === 'toggle_var' && (
        <>
          <div>
            <label style={FIELD_LABEL}>Variable</label>
            <select
              value={click.name || ''}
              onChange={(e) => setClick({ ...click, name: e.target.value })}
              style={INPUT}
            >
              <option value="">— Select —</option>
              {click.name && !runtime.getInputs().some((i) => i.name === click.name) && (
                <option value={click.name}>{click.name} (not defined)</option>
              )}
              {runtime.getInputs().map((i) => (
                <option key={i.name} value={i.name}>{i.label || i.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <div>
              <label style={FIELD_LABEL}>On value</label>
              <input value={click.on ?? ''} placeholder="true"
                onChange={(e) => setClick({ ...click, on: e.target.value || undefined })} style={INPUT} />
            </div>
            <div>
              <label style={FIELD_LABEL}>Off value</label>
              <input value={click.off ?? ''} placeholder="false"
                onChange={(e) => setClick({ ...click, off: e.target.value || undefined })} style={INPUT} />
            </div>
          </div>
        </>
      )}

      {click.kind === 'add_to_cart' && (
        <>
          <div>
            <label style={FIELD_LABEL}>Item code (SKU)</label>
            <input
              value={click.sku || ''}
              onChange={(e) => setClick({ ...click, sku: e.target.value })}
              placeholder="supports {{vars}} — one cart line per code"
              style={{ ...INPUT, fontFamily: 'monospace' }}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Name</label>
            <input
              value={click.name || ''}
              onChange={(e) => setClick({ ...click, name: e.target.value })}
              placeholder="What the cart calls it"
              style={INPUT}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Price</label>
            <input
              value={click.price ?? ''}
              onChange={(e) => setClick({ ...click, price: e.target.value })}
              placeholder="₹3,200"
              style={INPUT}
            />
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              Shown to the visitor and used for the cart's own totals. What is
              actually charged is always re-read from the saved order, so this
              can never set a price.
            </p>
          </div>
          <div style={SUBSECTION}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input
                type="checkbox" checked={!!click.qtyPicker}
                onChange={(e) => setClick({ ...click, qtyPicker: e.target.checked || undefined })}
              />
              Let the visitor choose the quantity
            </label>
            <div style={TWO_COL}>
              <div>
                <label style={FIELD_LABEL}>{click.qtyPicker ? 'Starts at' : 'Quantity added'}</label>
                <OptionalNumber
                  value={click.qty} min={1} step={1} placeholder="1"
                  onChange={(n) => setClick({ ...click, qty: n === undefined ? undefined : Math.max(1, Math.round(n)) })}
                />
              </div>
              {click.qtyPicker && (
                <div>
                  <label style={FIELD_LABEL}>Most per add</label>
                  <OptionalNumber
                    value={click.maxQty} min={1} step={1} placeholder="99"
                    onChange={(n) => setClick({ ...click, maxQty: n === undefined ? undefined : Math.max(1, Math.round(n)) })}
                  />
                </div>
              )}
            </div>
            {click.qtyPicker && (
              <div>
                <label style={FIELD_LABEL}>Caption (optional)</label>
                <input
                  value={click.qtyLabel ?? ''}
                  onChange={(e) => setClick({ ...click, qtyLabel: e.target.value || undefined })}
                  placeholder={click.booking ? 'Rooms' : 'Quantity'}
                  style={INPUT}
                />
              </div>
            )}
            <p style={HINT}>
              A − n + stepper sits beside the button; the visitor can still
              change it later in the cart.
            </p>
          </div>

          <BookingEditor
            booking={click.booking}
            onChange={(booking) => setClick({ ...click, booking })}
          />
          <div>
            <label style={FIELD_LABEL}>Second line (optional)</label>
            <input
              value={click.meta ?? ''}
              onChange={(e) => setClick({ ...click, meta: e.target.value || undefined })}
              placeholder="Orthopaedic · 30.5 cm"
              style={INPUT}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Links back to</label>
            <input
              value={click.href ?? ''}
              onChange={(e) => setClick({ ...click, href: e.target.value || undefined })}
              placeholder="/adson-beckman-retractor"
              style={{ ...INPUT, fontFamily: 'monospace' }}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Image URL (optional)</label>
            <input
              value={click.image ?? ''}
              onChange={(e) => setClick({ ...click, image: e.target.value || undefined })}
              style={{ ...INPUT, fontFamily: 'monospace' }}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Confirmation</label>
            <input
              value={click.addedLabel ?? ''}
              onChange={(e) => setClick({ ...click, addedLabel: e.target.value || undefined })}
              placeholder="Added to cart"
              style={INPUT}
            />
          </div>
          {/* Once the item is in the basket. The shared cart script reveals
              these on every surface, a published page included. */}
          <div style={SUBSECTION}>
            <label style={FIELD_LABEL}>When it is already in the cart</label>
            <input
              value={click.inCartLabel ?? ''}
              onChange={(e) => setClick({ ...click, inCartLabel: e.target.value === '' ? undefined : e.target.value })}
              placeholder="In cart · {n}"
              style={INPUT}
            />
            {!click.booking && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={click.inCartStepper ?? !click.qtyPicker}
                  onChange={(e) => setClick({ ...click, inCartStepper: e.target.checked === !click.qtyPicker ? undefined : e.target.checked })}
                />
                Swap the button for a − n + stepper that edits the cart line
              </label>
            )}
            <p style={HINT}>
              {'{n}'} is the quantity in the cart. Without the stepper, the
              text appears beside the button instead.
            </p>
          </div>
          <div style={SUBSECTION}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input
                type="checkbox" checked={click.toast !== false}
                onChange={(e) => setClick({ ...click, toast: e.target.checked ? undefined : false })}
              />
              Show an "Added to cart" notice
            </label>
            {click.toast !== false && (
              <>
                <div style={TWO_COL}>
                  <div>
                    <label style={FIELD_LABEL}>Notice</label>
                    <input value={click.toastLabel ?? ''} placeholder="Added to cart"
                      onChange={(e) => setClick({ ...click, toastLabel: e.target.value || undefined })} style={INPUT} />
                  </div>
                  <div>
                    <label style={FIELD_LABEL}>Link text</label>
                    <input value={click.toastLinkLabel ?? ''} placeholder="View cart"
                      onChange={(e) => setClick({ ...click, toastLinkLabel: e.target.value || undefined })} style={INPUT} />
                  </div>
                </div>
                <div>
                  <label style={FIELD_LABEL}>Cart page</label>
                  <input value={click.cartHref ?? ''} placeholder="/cart — no link when empty"
                    onChange={(e) => setClick({ ...click, cartHref: e.target.value || undefined })}
                    style={{ ...INPUT, fontFamily: 'monospace' }} />
                </div>
              </>
            )}
          </div>
          <div style={SUBSECTION}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input
                type="checkbox" checked={!!click.wishlist}
                onChange={(e) => setClick({ ...click, wishlist: e.target.checked || undefined })}
              />
              Heart beside the button (save to wishlist)
            </label>
            {click.wishlist && (
              <div style={TWO_COL}>
                <div>
                  <label style={FIELD_LABEL}>Not saved</label>
                  <input value={click.wishlistAddLabel ?? ''} placeholder="Add to wishlist"
                    onChange={(e) => setClick({ ...click, wishlistAddLabel: e.target.value || undefined })} style={INPUT} />
                </div>
                <div>
                  <label style={FIELD_LABEL}>Saved</label>
                  <input value={click.wishlistRemoveLabel ?? ''} placeholder="Saved to wishlist"
                    onChange={(e) => setClick({ ...click, wishlistRemoveLabel: e.target.value || undefined })} style={INPUT} />
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {click.kind === 'copy' && (
        <>
          <div>
            <label style={FIELD_LABEL}>Text to copy</label>
            <input
              value={click.text || ''}
              onChange={(e) => setClick({ ...click, text: e.target.value })}
              placeholder="supports {{vars}}"
              style={INPUT}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Confirmation</label>
            <input
              value={click.confirm ?? ''}
              onChange={(e) => setClick({ ...click, confirm: e.target.value || undefined })}
              placeholder="Copied"
              style={INPUT}
            />
          </div>
        </>
      )}

      {click.kind === 'sign_out' && (
        <div>
          <label style={FIELD_LABEL}>Then go to</label>
          <input
            value={click.redirect ?? ''}
            onChange={(e) => setClick({ kind: 'sign_out', redirect: e.target.value || undefined })}
            placeholder="/ (blank = stay on this page)"
            style={INPUT}
          />
        </div>
      )}

      {click.kind === 'play_media' && (
        <>
          <div>
            <label style={FIELD_LABEL}>Video block id</label>
            <input
              value={click.blockId || ''}
              onChange={(e) => setClick({ ...click, blockId: e.target.value })}
              placeholder="id of a video block on this page"
              style={{ ...INPUT, fontFamily: 'monospace' }}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Action</label>
            <select
              value={click.action || 'toggle'}
              onChange={(e) => setClick({ ...click, action: e.target.value as 'play' | 'pause' | 'toggle' })}
              style={INPUT}
            >
              <option value="toggle">Toggle</option>
              <option value="play">Play</option>
              <option value="pause">Pause</option>
            </select>
          </div>
        </>
      )}

      {click.kind === 'navigate' && (
        <NavigateEditor click={click} setClick={setClick} navTargets={navTargets} />
      )}

      {click.kind === 'set_inputs' && (
        <SetInputsEditor
          values={click.values}
          onChange={(values) => setClick({ ...click, values })}
        />
      )}

      {click.kind === 'run_action' && (
        <div>
          <label style={FIELD_LABEL}>Action</label>
          <select
            value={click.actionName || ''}
            onChange={(e) => setClick({ ...click, actionName: e.target.value })}
            style={INPUT}
          >
            <option value="">— Select —</option>
            {click.actionName && !actions.some((a) => a.name === click.actionName) && (
              <option value={click.actionName}>{click.actionName} (not defined)</option>
            )}
            {actions.map((a) => (
              <option key={a.id} value={a.name}>{a.name}</option>
            ))}
          </select>
          {click.actionName && !actions.some((a) => a.name === click.actionName) && (
            <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-error, #cf222e)' }}>
              Action "{click.actionName}" is not defined on this page.
            </div>
          )}
          {actions.length === 0 && (
            <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              No page actions defined yet. Add one in Page properties → Actions.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

type CartBookingSpec = NonNullable<Extract<ButtonClick, { kind: 'add_to_cart' }>['booking']>

/**
 * The calendar side of an Add-to-cart: a stay or a rental sold by the night
 * or by the day. Off, the button sells units; on, a range calendar renders
 * above it and the price becomes per night / per day. Placeholders are the
 * runtime's own defaults (cart.ts `cfgOf`).
 */
function BookingEditor({ booking, onChange }: {
  booking: CartBookingSpec | undefined
  onChange: (next: CartBookingSpec | undefined) => void
}) {
  const on = !!booking
  const b: CartBookingSpec = booking || {}
  const upd = (patch: Partial<CartBookingSpec>) => onChange({ ...b, ...patch })
  const perDay = b.unit === 'day'
  const intOrUndef = (n: number | undefined, floor: number) =>
    n === undefined ? undefined : Math.max(floor, Math.round(n))
  return (
    <div style={SUBSECTION}>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input
          type="checkbox" checked={on}
          onChange={(e) => onChange(e.target.checked ? { unit: 'night' } : undefined)}
        />
        Book dates (hotel stay, rental)
      </label>
      {on && (
        <>
          <div>
            <label style={FIELD_LABEL}>Price is per</label>
            <select
              value={perDay ? 'day' : 'night'}
              onChange={(e) => upd({ unit: e.target.value === 'day' ? 'day' : 'night' })}
              style={INPUT}
            >
              <option value="night">Night — check-out day is free (hotel, homestay)</option>
              <option value="day">Day — the last day counts (vehicle, equipment)</option>
            </select>
          </div>
          <div style={TWO_COL}>
            <div>
              <label style={FIELD_LABEL}>Start label</label>
              <input value={b.startLabel ?? ''} placeholder={perDay ? 'Pick-up' : 'Check-in'}
                onChange={(e) => upd({ startLabel: e.target.value || undefined })} style={INPUT} />
            </div>
            <div>
              <label style={FIELD_LABEL}>End label</label>
              <input value={b.endLabel ?? ''} placeholder={perDay ? 'Return' : 'Check-out'}
                onChange={(e) => upd({ endLabel: e.target.value || undefined })} style={INPUT} />
            </div>
          </div>
          <div style={TWO_COL}>
            <div>
              <label style={FIELD_LABEL}>Shortest ({perDay ? 'days' : 'nights'})</label>
              <OptionalNumber value={b.minUnits} min={1} step={1} placeholder="1"
                onChange={(n) => upd({ minUnits: intOrUndef(n, 1) })} />
            </div>
            <div>
              <label style={FIELD_LABEL}>Longest ({perDay ? 'days' : 'nights'})</label>
              <OptionalNumber value={b.maxUnits} min={1} step={1} placeholder="No limit"
                onChange={(n) => upd({ maxUnits: intOrUndef(n, 1) })} />
            </div>
          </div>
          <div>
            <label style={FIELD_LABEL}>Bookable how far ahead (days)</label>
            <OptionalNumber value={b.maxAdvanceDays} min={0} step={1} placeholder="365"
              onChange={(n) => upd({ maxAdvanceDays: intOrUndef(n, 0) })} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Unavailable dates</label>
            <textarea
              value={b.blockedDates ?? ''} rows={2}
              placeholder={'2026-12-24, 2026-12-25 or {{soldOut}}'}
              onChange={(e) => upd({ blockedDates: e.target.value || undefined })}
              style={{ ...INPUT, resize: 'vertical', fontFamily: 'monospace' }}
            />
            <p style={HINT}>
              Dates as YYYY-MM-DD, separated by commas or new lines. A page
              variable ({'{{soldOut}}'}) can supply them from your data. These are
              greyed out for the visitor only — check availability in the
              order's own logic when it is saved.
            </p>
          </div>
          <div style={TWO_COL}>
            <div>
              <label style={FIELD_LABEL}>Default start</label>
              <input value={b.defaultStart ?? ''} placeholder="{{checkin}}"
                onChange={(e) => upd({ defaultStart: e.target.value || undefined })}
                style={{ ...INPUT, fontFamily: 'monospace' }} />
            </div>
            <div>
              <label style={FIELD_LABEL}>Default end</label>
              <input value={b.defaultEnd ?? ''} placeholder="{{checkout}}"
                onChange={(e) => upd({ defaultEnd: e.target.value || undefined })}
                style={{ ...INPUT, fontFamily: 'monospace' }} />
            </div>
          </div>
          <p style={HINT}>
            The price above becomes the rate per {perDay ? 'day' : 'night'}; the
            cart charges rate × {perDay ? 'days' : 'nights'} × quantity. Each
            date range is its own cart line. Map the dates onto the order line
            in the page variable that is filled from the cart.
          </p>
        </>
      )}
    </div>
  )
}

function SetInputsEditor({
  values, onChange,
}: { values: Record<string, string>; onChange: (v: Record<string, string>) => void }) {
  const entries = Object.entries(values || {})
  return (
    <div>
      <label style={FIELD_LABEL}>Inputs to set</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {entries.map(([k, v], i) => (
          <div key={i} style={{ display: 'flex', gap: 6 }}>
            <input
              value={k}
              onChange={(e) => {
                const next: Record<string, string> = {}
                entries.forEach(([kk, vv], j) => { next[j === i ? e.target.value : kk] = vv })
                onChange(next)
              }}
              placeholder="input_name"
              style={{ ...INPUT, flex: 1, fontFamily: 'monospace' }}
            />
            <input
              value={v}
              onChange={(e) => {
                const next = { ...values, [k]: e.target.value }
                onChange(next)
              }}
              placeholder="value (supports {{vars}})"
              style={{ ...INPUT, flex: 2 }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation()
                const next = { ...values }
                delete next[k]
                onChange(next)
              }}
              className="btn btn-ghost btn-sm"
              style={{ padding: 4, color: 'var(--color-error)' }}
              title="Remove"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onChange({ ...values, [`input_${entries.length + 1}`]: '' })
          }}
          className="btn btn-ghost btn-sm"
          style={{ alignSelf: 'flex-start' }}
        >
          <Plus size={13} /> Add row
        </button>
      </div>
    </div>
  )
}

// ─── Navigate editor ─────────────────────────────────────────
// Beyond obj_type / obj_name, this editor lets the author pick:
//   - mode (list / add / edit) when navigating to a Transaction
//   - args[]: page input → destination column / criterion mappings
// Available targets are loaded from the destination's saved JSON via the
// runtime's project context. Falls back to free-text target entry when
// the destination metadata is unknown.

type NavigateClick = Extract<ButtonClick, { kind: 'navigate' }>

function NavigateEditor({
  click,
  setClick,
  navTargets,
}: {
  click: NavigateClick
  setClick: (next: NavigateClick) => void
  navTargets?: { obj_type: string; obj_name: string; title?: string }[]
}) {
  const runtime = usePageRuntime()
  const project = runtime.getProject()
  const inputs = runtime.getInputs()
  const [model, setModel] = useState<unknown>(null)

  // Catalog of existing objects in this project. We merge two sources:
  //   1. application.json navigation entries (covers T / C / W / R / G — all
  //      registered top-level objects, including pages and reports).
  //   2. project-folder scans for D and V (those don't always appear in
  //      application.json yet — Dashboard/Visual designers persist directly
  //      to disk before the navigation entry is wired up).
  // The user can still type a custom name for "I'll create this later".
  type CatalogEntry = { obj_name: string; title: string }
  const [catalog, setCatalog] = useState<Record<string, CatalogEntry[]>>({})

  useEffect(() => {
    if (!project) return
    let cancelled = false
    const merged: Record<string, CatalogEntry[]> = {}
    const seen: Record<string, Set<string>> = {}
    const push = (type: string, entry: CatalogEntry) => {
      if (!entry.obj_name) return
      const key = type
      if (!seen[key]) seen[key] = new Set()
      if (seen[key].has(entry.obj_name)) return
      seen[key].add(entry.obj_name)
      if (!merged[key]) merged[key] = []
      merged[key].push(entry)
    }
    // 1. Navigation entries from application.json — pre-seed with the
    //    `navTargets` prop when the parent already has them, plus a fresh
    //    fetch as a fallback.
    if (navTargets && navTargets.length > 0) {
      navTargets.forEach((n) => push(n.obj_type, { obj_name: n.obj_name, title: n.title || '' }))
    }
    Promise.all([
      getJsonRaw(project).then((res: { navigation?: Array<{ obj_type: string; obj_name: string; title?: string }> }) => {
        ;(res.navigation || []).forEach((n) => push(n.obj_type, { obj_name: n.obj_name, title: n.title || '' }))
      }).catch(() => {}),
      // 2. Dashboard / Visual / Smart Page project-folder scans
      listProjectDashboards(project).then((res) => {
        ;(res.dashboards || []).forEach((d) => push('D', {
          obj_name: d.obj_name, title: d.dashboard_name || d.obj_name,
        }))
      }).catch(() => {}),
      listProjectVisuals(project).then((res) => {
        ;(res.visuals || []).forEach((v) => push('V', {
          obj_name: v.obj_name,
          title: v.visual_type ? `${v.obj_name} (${v.visual_type})` : v.obj_name,
        }))
      }).catch(() => {}),
      listProjectPages(project).then((res) => {
        ;(res.pages || []).forEach((p) => push('S', {
          obj_name: p.obj_name, title: p.title || p.obj_name,
        }))
      }).catch(() => {}),
    ]).then(() => {
      if (cancelled) return
      // Sort each bucket alphabetically for predictable dropdowns
      Object.keys(merged).forEach((k) => {
        merged[k].sort((a, b) => a.obj_name.localeCompare(b.obj_name))
      })
      setCatalog(merged)
    })
    return () => { cancelled = true }
  }, [project, navTargets])

  // Load destination metadata so the arg-row dropdown has real columns.
  // Skipped for object types we don't have a metadata loader for.
  useEffect(() => {
    setModel(null)
    if (!project || !click.obj_name) return
    let cancelled = false
    const loader =
      click.obj_type === 'T' ? loadTransactionObject(project, click.obj_name)
      : click.obj_type === 'D' ? loadDashboardObject(project, click.obj_name)
      : click.obj_type === 'V' ? loadVisualObject(project, click.obj_name)
      : click.obj_type === 'S' ? loadPageObject(project, click.obj_name)
      : null
    if (!loader) return
    loader
      .then((res) => { if (!cancelled) setModel(res?.content ?? null) })
      .catch(() => { if (!cancelled) setModel(null) })
    return () => { cancelled = true }
  }, [project, click.obj_type, click.obj_name])

  const argTargets: ArgTargetOption[] = useMemo(() => {
    if (!model) return []
    if (click.obj_type === 'T') {
      const root = model as Record<string, unknown>
      const inner = (root.transaction as Record<string, unknown>) || root
      const formIdx = click.form_no ? Math.max(0, parseInt(click.form_no, 10) - 1) : 0
      const sqlModels = inner.sql_models as Array<Record<string, unknown>> | undefined
      const sqlModel = sqlModels?.[formIdx]?.sql_model as Record<string, unknown> | undefined
      const cols = (sqlModel?.columns as Array<{ column: { name: string; heading?: string; col_type?: string } }>) || []
      return cols.map((c) => ({ name: c.column.name, label: c.column.heading, hint: c.column.col_type }))
    }
    if (click.obj_type === 'D') return extractDashboardTargets(model)
    if (click.obj_type === 'V') return extractVisualTargets(model)
    if (click.obj_type === 'S') return extractPageTargets(model)
    return []
  }, [model, click.obj_type, click.form_no])

  // Reset mode/form_no/args whenever the destination type changes — those
  // fields are type-specific and stale data confuses the runtime.
  const setObjType = (obj_type: string) => {
    setClick({
      ...click,
      obj_type,
      obj_name: '',
      mode: undefined,
      form_no: undefined,
      args: undefined,
    })
  }

  const setObjName = (obj_name: string) => {
    setClick({ ...click, obj_name, args: undefined, form_no: undefined })
  }

  const setMode = (mode: EmbedMode | '') => {
    setClick({ ...click, mode: mode || undefined })
  }

  const setArgs = (args: ArgMapping[]) => {
    setClick({ ...click, args: args.length ? args : undefined })
  }

  const sqlModelsCount = useMemo(() => {
    if (click.obj_type !== 'T' || !model) return 0
    const root = model as Record<string, unknown>
    const inner = (root.transaction as Record<string, unknown>) || root
    const arr = inner.sql_models as unknown[] | undefined
    return Array.isArray(arr) ? arr.length : 0
  }, [click.obj_type, model])

  return (
    <>
      <div>
        <label style={FIELD_LABEL}>Object type</label>
        <select
          value={click.obj_type}
          onChange={(e) => setObjType(e.target.value)}
          style={INPUT}
        >
          <option value="C">Page (C)</option>
          <option value="T">Transaction (T)</option>
          <option value="W">Workflow (W)</option>
          <option value="R">Report (R)</option>
          <option value="D">Dashboard (D)</option>
          <option value="V">Visual (V)</option>
          <option value="S">Smart Page (S)</option>
          <option value="G">General process (G)</option>
        </select>
      </div>

      <ObjectPickerField
        label="Object name"
        value={click.obj_name}
        onChange={setObjName}
        catalog={catalog[click.obj_type] || []}
        typeLabel={TYPE_LABEL[click.obj_type] || 'object'}
        emptyHint={`No ${TYPE_LABEL[click.obj_type] || 'object'}s found in this project — use a custom name to reference one you'll create later.`}
      />

      {/* Transaction-only: choose which screen to land on */}
      {click.obj_type === 'T' && (
        <div>
          <label style={FIELD_LABEL}>Action</label>
          <select
            value={click.mode || ''}
            onChange={(e) => setMode(e.target.value as EmbedMode | '')}
            style={INPUT}
          >
            <option value="">Default landing</option>
            <option value="list">List records</option>
            <option value="add">Add new record</option>
            <option value="edit">Edit existing record</option>
          </select>
        </div>
      )}

      {click.obj_type === 'T' && sqlModelsCount > 1 && (
        <div>
          <label style={FIELD_LABEL}>Form #</label>
          <select
            value={click.form_no || '1'}
            onChange={(e) => setClick({ ...click, form_no: e.target.value })}
            style={INPUT}
          >
            {click.form_no && !(Number(click.form_no) >= 1 && Number(click.form_no) <= sqlModelsCount
              && String(Number(click.form_no)) === click.form_no) && (
              <option value={click.form_no}>{click.form_no} (not in this transaction)</option>
            )}
            {Array.from({ length: sqlModelsCount }).map((_, i) => (
              <option key={i} value={String(i + 1)}>{i + 1}</option>
            ))}
          </select>
        </div>
      )}
      {/* A saved form # stays visible (and editable) when the destination's
          model is not loaded, or no longer has several forms. */}
      {click.form_no && !(click.obj_type === 'T' && sqlModelsCount > 1) && (
        <div>
          <label style={FIELD_LABEL}>Form #</label>
          <input
            value={click.form_no}
            onChange={(e) => setClick({ ...click, form_no: e.target.value.trim() || undefined })}
            placeholder="1"
            style={{ ...INPUT, fontFamily: 'monospace' }}
          />
        </div>
      )}

      {/* Argument mapping — passed to the destination on navigate */}
      {(click.obj_type === 'T' || click.obj_type === 'D' || click.obj_type === 'V' || click.obj_type === 'S') && (
        <ArgMappingEditor
          title={
            click.obj_type === 'D' ? 'Dashboard arguments'
            : click.obj_type === 'V' ? 'Visual arguments'
            : click.obj_type === 'S' ? 'Page input values'
            : click.mode === 'list' ? 'List filters (column = value)'
            : click.mode === 'add' ? 'Pre-fill values (Add)'
            : click.mode === 'edit' ? 'Record-id / pre-fill values (Edit)'
            : 'Arguments'
          }
          args={click.args}
          inputs={inputs}
          project={project}
          availableTargets={argTargets}
          metaUnavailableHint={
            !click.obj_name
              ? 'Pick an object name first.'
              : argTargets.length === 0
                ? 'Destination metadata not yet available — type the target name freehand.'
                : undefined
          }
          emptyHint={
            click.obj_type === 'T'
              ? 'No arguments. Add a row to pass a page input as a column value.'
              : click.obj_type === 'S'
                ? "No values. Add a row to seed a target page input from this page's variables."
                : 'No arguments. Add a row to map a page input to a destination criterion.'
          }
          onChange={setArgs}
        />
      )}
    </>
  )
}

// Friendly type label used by ObjectPickerField placeholders / hints.
const TYPE_LABEL: Record<string, string> = {
  T: 'transaction',
  C: 'page',
  W: 'workflow',
  R: 'report',
  D: 'dashboard',
  V: 'visual',
  S: 'smart page',
  G: 'general process',
}
